package com.evoapps.evoshift.ui.map

import androidx.compose.ui.graphics.ImageBitmap
import com.evoapps.evoshift.photo.decodeImage
import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.statement.readRawBytes
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.sync.Semaphore
import kotlinx.coroutines.sync.withPermit
import kotlinx.coroutines.withContext

/** The images of the map (SH-132), one tile at a time; null when a tile cannot be had. */
interface MapTileSource {
    /** A tile already in memory, without waiting. */
    fun cached(tile: TileKey): ImageBitmap?

    suspend fun load(tile: TileKey): ImageBitmap?
}

/** No images (UI tests): the map draws its empty grid, the pin and the attribution. */
object NoMapTiles : MapTileSource {
    override fun cached(tile: TileKey): ImageBitmap? = null
    override suspend fun load(tile: TileKey): ImageBitmap? = null
}

/**
 * OpenStreetMap's standard tiles (https://operations.osmfoundation.org/policies/tiles/): the
 * documented URL, an identifying User-Agent where the platform lets the app set one (browsers
 * send their own and the page's Referer), only the tiles in view and only while the map is shown
 * (no prefetching, no bulk download), at most [PARALLEL] downloads at once, and every tile kept
 * in memory for the session ([CAPACITY], least recently used dropped) so it is fetched once. A
 * tile that failed is not asked again in this session. The map shows the required attribution.
 * Heavier use would need a commercial tile provider: change [URL] (and the privacy policy).
 */
class OsmTileSource(private val httpClient: HttpClient) : MapTileSource {
    private val images = LinkedHashMap<TileKey, ImageBitmap>()
    private val failed = mutableSetOf<TileKey>()
    private val downloads = Semaphore(PARALLEL)

    override fun cached(tile: TileKey): ImageBitmap? = images[tile]?.also { images.remove(tile); images[tile] = it }

    override suspend fun load(tile: TileKey): ImageBitmap? {
        cached(tile)?.let { return it }
        if (tile in failed) return null
        val image = try {
            downloads.withPermit {
                // Another view of the same tile may have finished while this one waited.
                cached(tile)?.let { return it }
                val response = httpClient.get(url(tile)) { mapTileUserAgent?.let { header(HttpHeaders.UserAgent, it) } }
                if (!response.status.isSuccess()) null
                else response.readRawBytes().let { bytes ->
                    withContext(Dispatchers.Default) { runCatching { decodeImage(bytes, TILE_PIXELS) }.getOrNull() }
                }
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            null
        }
        if (image == null) failed += tile else put(tile, image)
        return image
    }

    private fun put(tile: TileKey, image: ImageBitmap) {
        images.remove(tile)
        images[tile] = image
        while (images.size > CAPACITY) images.remove(images.keys.first())
    }

    companion object {
        const val URL = "https://tile.openstreetmap.org"
        const val TILE_PIXELS = 256
        const val PARALLEL = 2
        const val CAPACITY = 256

        fun url(tile: TileKey) = "$URL/${tile.zoom}/${tile.x}/${tile.y}.png"
    }
}

/** What the app calls itself to the tile servers; null where the browser sets it. */
internal const val EVOSHIFT_USER_AGENT = "EVoShift/1.0 (+https://evoshift.app; contact@evoshift.app)"

internal expect val mapTileUserAgent: String?
